import { useEffect } from "react";

export interface NoIndexSeoProps {
  title: string;
  description: string;
  path: `/${string}`;
  baseUrl?: string;
}

type Restore = () => void;

const setNamedMeta = (name: string, content: string): Restore => {
  const existing = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  const element = existing ?? document.createElement("meta");
  const previous = existing?.getAttribute("content") ?? null;

  if (!existing) {
    element.setAttribute("name", name);
    document.head.appendChild(element);
  }

  element.setAttribute("content", content);

  return () => {
    if (!existing) element.remove();
    else if (previous === null) element.removeAttribute("content");
    else element.setAttribute("content", previous);
  };
};

const setCanonical = (href: string): Restore => {
  const existing = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  const element = existing ?? document.createElement("link");
  const previous = existing?.getAttribute("href") ?? null;

  if (!existing) {
    element.setAttribute("rel", "canonical");
    document.head.appendChild(element);
  }

  element.setAttribute("href", href);

  return () => {
    if (!existing) element.remove();
    else if (previous === null) element.removeAttribute("href");
    else element.setAttribute("href", previous);
  };
};

export const NoIndexSeo = ({
  title,
  description,
  path,
  baseUrl = "https://myelinahealth.com",
}: NoIndexSeoProps) => {
  useEffect(() => {
    const previousTitle = document.title;
    const canonical = `${baseUrl.replace(/\/+$/, "")}${path}`;
    document.title = title;

    const restores = [
      setNamedMeta("description", description),
      setNamedMeta("robots", "noindex, follow"),
      setCanonical(canonical),
    ];

    return () => {
      for (let i = restores.length - 1; i >= 0; i--) restores[i]();
      document.title = previousTitle;
    };
  }, [title, description, path, baseUrl]);

  return null;
};

export default NoIndexSeo;
